/**
 * Detects auth-failure errors thrown by Convex queries/mutations.
 *
 * When the Convex auth token drops (sign-out, token expiry after laptop
 * sleep), the server re-executes every SUBSCRIBED query without auth and
 * pushes the resulting errors into still-mounted useQuery hooks, which throw
 * them during render. This can happen BEFORE the client's
 * useConvexAuth().isAuthenticated flips, so `'skip'` gating alone cannot
 * prevent it, these errors must also be caught and recovered from
 * (see AuthErrorBoundary).
 *
 * Matches the messages our backend throws when getUserIdentity() is null
 * ("Authentication required", "Not authenticated") plus Convex platform
 * token-verification failures. Deliberately does NOT match "Unauthorized" /
 * permission errors, those are real authz failures for a signed-in user.
 */
export function isAuthError(error: unknown): boolean {
  if (!error) return false
  const parts: string[] = []
  if (error instanceof Error && typeof error.message === 'string') {
    parts.push(error.message)
  }
  // ConvexError carries the thrown value in `data` (a string in our backend).
  const data = (error as { data?: unknown }).data
  if (typeof data === 'string') {
    parts.push(data)
  } else if (
    data &&
    typeof (data as { message?: unknown }).message === 'string'
  ) {
    parts.push((data as { message: string }).message)
  }
  const text = parts.join(' ')
  return /authentication required|not authenticated|no auth provider found|could not verify token/i.test(
    text,
  )
}
